import { ReactNode, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  initialQueryState,
  OptionsHelper,
  showError,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { getEmpresaById } from '../../../empresas-list/core/_requests'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: botão "Adicionar"). */
  actions?: ReactNode
}

export const ClientesListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const { id } = useParams()
  const total = response?.totalElements ?? 0

  // Rota clientes/:id já chega com a instituição selecionada.
  useEffect(() => {
    if (!id) return
    getEmpresaById(Number(id))
      .then((empresa) =>
        setFilter('empresa', { label: String(empresa.nomeFantasia), value: Number(empresa.id) })
      )
      .catch(showError)
  }, [id, setFilter])

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(() => {
    return [
      debouncedFilters.status ? `ativa:${debouncedFilters.status.value}` : '',
      debouncedFilters.empresa ? `superior:${debouncedFilters.empresa.value}` : '',
    ]
      .filter(Boolean)
      .join(',')
  }, [debouncedFilters])

  useEffect(() => {
    updateState({
      search,
      ...initialQueryState,
    })
  }, [search, updateState])

  const extraParams = useMemo(() => ({ onlySuperior: true }), [])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
    filters.status && {
      key: 'status',
      label: t('situacao.label'),
      value: filters.status.label,
      onRemove: () => setFilter('status', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <TableFilters
      actions={actions}
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            !filters.empresa
              ? undefined
              : isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'entidade encontrada' : 'entidades encontradas'}`
          }
        />
      }
    >
      <TableFilter label={t('empresa.label')} wide>
        <CustomSelect
          entity='empresa'
          onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsEmpresasAdmin}
          fetchOptionsParams={extraParams}
          value={filters.empresa}
        />
      </TableFilter>
      <TableFilter label={t('situacao.label')}>
        <CustomSelect
          entity='situacao'
          onChange={(status?: IOption<boolean>) => setFilter('status', status ?? undefined)}
          options={OptionsHelper.statusCadastro}
          value={filters.status}
        />
      </TableFilter>
    </TableFilters>
  )
}
